<template>
  <div class="flex min-w-0 items-center gap-3">
    <!-- Up one level, not browser history: history can leave the app, or land far from where you were. -->
    <TwnBaseBtn
      v-if="backCrumb"
      v-tooltip.bottom="`Back to ${backCrumb.label}`"
      :aria-label="`Back to ${backCrumb.label}`"
      :route-url="backCrumb.url"
      icon="arrow_back"
      severity="secondary"
      size="small"
      variant="outlined"
    />
    <Breadcrumb
      :home="breadcrumbHome"
      :model="crumbs"
      :pt="breadcrumbPt"
    >
      <template #item="{ item }">
        <!-- As the page header the last crumb is the page's name, so it reads as a title, not a link. -->
        <span
          v-if="isTitleCrumb(item)"
          aria-current="page"
          class="text-[14.5px] font-semibold text-surface-900"
        >
          {{ item.label }}
        </span>
        <!-- Parent crumbs in the header are quiet links with no box, so only the back button reads as one. -->
        <Link
          v-else-if="isLinkCrumb(item)"
          :href="item.url"
          class="rounded text-[14.5px] font-medium text-surface-500 transition-colors hover:text-surface-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
        >
          {{ item.label }}
        </Link>
        <span
          v-else-if="isTextCrumb(item)"
          class="text-[14.5px] font-medium text-surface-500"
        >
          {{ item.label }}
        </span>
        <TwnBaseBtn
          v-else
          :aria-label="crumbAriaLabel(item)"
          :icon="item.icon"
          :label="item.label"
          :route-url="item.url"
          :variant="crumbVariant(item)"
          severity="secondary"
          size="small"
        />
      </template>
      <template
        v-if="isPageHeader"
        #separator
      >
        <span
          aria-hidden="true"
          class="text-[14.5px] font-light text-surface-300"
          >/</span
        >
      </template>
    </Breadcrumb>
  </div>
</template>
<script lang="ts" setup>
import type { MenuItem } from 'primevue/menuitem'

import { Link } from '@inertiajs/vue3'
import Breadcrumb from 'primevue/breadcrumb'
import { computed, ref } from 'vue'

import type { TwnBreadcrumbsProps } from '@/components/atoms/breadcrumbs/twn-breadcrumbs/TwnBreadcrumbs.types'

import TwnBaseBtn from '@/components/atoms/buttons/twn-base-btn/TwnBaseBtn.vue'
import { useBreadcrumbStore } from '@/domains/admin-panel/store/use-breadcrumb-store/useBreadcrumbStore.ts'
import { TWINE_ROUTES } from '@/types/const/routes.consts.ts'

// The focus layout's header: a back button to the parent, slashes, and the current page set as a title.
const props = withDefaults(defineProps<TwnBreadcrumbsProps>(), { isPageHeader: false })

const home = ref({ icon: 'home', url: TWINE_ROUTES.HOME.ROOT })

// Get breadcrumb store
const breadcrumbStore = useBreadcrumbStore()

const crumbs = computed(() => (props.isPageHeader ? breadcrumbStore.headerItems : breadcrumbStore.breadcrumbItems))

// The header draws no home icon; the back button and the trail say where you are.
const breadcrumbHome = computed(() => (props.isPageHeader ? undefined : home.value))

const breadcrumbPt = computed(() => ({
  root: {
    style: {
      padding: 0,
      // Prime paints a white box, which shows on any ground that is not white.
      background: 'transparent'
    }
  },
  separator: {
    style: {
      // Header: the list's 0.5rem gap plus this makes 12px a side, the back button's own gap-3.
      margin: props.isPageHeader ? '0 0.25rem' : '0 0.15rem'
    }
  },
  separatorIcon: {
    style: {
      width: '0.8rem',
      height: '0.8rem'
    }
  }
}))

const isCurrent = (item: MenuItem): boolean => item === crumbs.value.at(-1)
const isTitleCrumb = (item: MenuItem): boolean => props.isPageHeader && isCurrent(item)
const isLinkCrumb = (item: MenuItem): boolean => props.isPageHeader && !!item.label && !!item.url
const isTextCrumb = (item: MenuItem): boolean => props.isPageHeader && !!item.label
// An icon-only crumb is the home button, so it names itself.
const crumbAriaLabel = (item: MenuItem): string | undefined => (item.label ? undefined : 'Home')
const crumbVariant = (item: MenuItem): 'outlined' | 'text' => (props.isPageHeader && !item.label ? 'outlined' : 'text')

// The nearest parent crumb that links somewhere; a top-level page has none, so it shows no back button.
const backCrumb = computed(() =>
  props.isPageHeader ? crumbs.value.slice(0, -1).findLast(crumb => crumb.url) : undefined
)
</script>
